"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import BannerFloatingArrow from "../uiDecor/bannerFloatingArrow";
import BottomCurves from "../curves/bottomCurves";

export default function WorkBanner({ data }: any) {
    const containerRef = useRef(null);
    const pathname = usePathname();

    const generateBreadcrumb = () => {
        const pathSegments = pathname.split("/").filter(Boolean);
        const breadcrumbs = [{ name: "Home", href: "/" }];

        pathSegments.forEach((segment, index) => {
            const href = "/" + pathSegments.slice(0, index + 1).join("/");
            const name = segment
                .split("-")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
            breadcrumbs.push({ name, href });
        });

        // The slug title-cases each word ("dksh" → "Dksh"). The banner header
        // is the CMS title, so acronyms such as DKSH keep their own casing.
        const cmsTitle =
            typeof data?.header === "string"
                ? data.header.replace(/\u00a0/g, " ").trim()
                : "";
        if (cmsTitle && breadcrumbs.length > 1) {
            breadcrumbs[breadcrumbs.length - 1].name = cmsTitle;
        }

        return breadcrumbs;
    };

    const breadcrumbs = generateBreadcrumb();

    useGSAP(
        () => {
            gsap.to(".textChild", { opacity: 1, stagger: 0.3, y: 0 });
        },
        { scope: containerRef },
    );

    return (
        <section
            className={`min-h-[360px] pb-[8.333vw] md:pb-[3.333vw] pt-24 md:pt-[8.333vw] flex flex-col md:items-center justify-center z-20  bg-midGradientPurple`}
        >
            <div className="flex flex-col md:flex-row" ref={containerRef}>
                <div className="flex-1">
                    <nav className="textChild opacity-0 translate-y-8 mb-12">
                        <ol className="flex items-start text-sm text-white/70">
                            {breadcrumbs.map((crumb, index) => (
                                <li
                                    key={crumb.href}
                                    className="flex items-start"
                                >
                                    {index > 0 && (
                                        <span className="mx-2">&gt;</span>
                                    )}
                                    {index === breadcrumbs.length - 1 ? (
                                        <span className="text-white">
                                            {crumb.name}
                                        </span>
                                    ) : (
                                        <Link
                                            href={crumb.href}
                                            className="hover:text-white transition-colors"
                                        >
                                            {crumb.name}
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ol>
                    </nav>
                    {data.header && (
                        <h1 className="textChild opacity-0 text-10xl md:w-[80%]">
                            {data.header}
                        </h1>
                    )}
                    {data.serviceName && (
                        <h6 className="textChild opacity-0 translate-y-8">
                            {data.serviceName}
                        </h6>
                    )}
                </div>
                <div className="flex-1">
                    {data.description && (
                        <p className="textChild text-white opacity-0 translate-y-8 md:pt-20">
                            {data.description}
                        </p>
                    )}
                </div>
            </div>
            <BannerFloatingArrow
                direction={"right"}
                className="!-bottom-16 md:!-bottom-20 lg:!-bottom-28 right-[10.625vw]"
                arrowtype="scroll"
            />

            <BottomCurves
                color="dark purple inverted"
                direction="right"
                className="flip-x mb-[1.5px] md:!mb-1"
            />
        </section>
    );
}
